import React from 'react';
import {findKey} from 'lodash';
import {get} from 'lodash';

import NavigationHeader from '/component/layout/NavigationHeader';
import {PeopleToContact} from '/component/partial/PeopleToContact';
import ClientsListHeader from '/component/partial/report/ClientsListHeader';
import ReportDataProvider from '/component/partial/report/ReportDataProvider';
import {CLIENT_TO_CONTACT_REPORT_ENUM} from '/enum/peopleToContact.enum';
import {REPORT_CLIENT_TO_CONTACT_NO_ORDER} from '/enum/records.enum';
import {SCREEN_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {shouldReleaseFeature} from '/util/featureRelease.util';

import {styles} from './PeopleToContactContent.styles';
import {generateOnRecordPress} from './PeopleToContactReport.util';

import {RecordsSortContext} from '/context/RecordsSort/RecordsSort';

interface PeopleToContactContentProps {
  recordsShowRecord: Function;
  currentScreen: string;
  noOrderMeta: {page: {total: number}};
  expiringRewardsMeta: {page: {total: number}};
  recentlyInactiveMeta: {page: {total: number}};
  premierCancellationsMeta: {page: {total: number}};
  expiringWellnessCreditsMeta: {page: {total: number}};
  nextTaskMeta: {page: {total: number}};
  noActiveOrder: {page: {total: number}};
  newCandidateMeta: {page: {total: number}};
  noRXOrderMeta: {page: {total: number}};
  featureFlag: {};
  filterName?: string;
}
export const PeopleToContactContent: React.FC<
  PeopleToContactContentProps
> = props => {
  const {activeRecordsName, setActiveRecordsName} =
    React.useContext(RecordsSortContext);
  const {
    recordsShowRecord,
    currentScreen,
    featureFlag,
    noRXOrderMeta,
    noOrderMeta,
    expiringRewardsMeta,
    recentlyInactiveMeta,
    premierCancellationsMeta,
    expiringWellnessCreditsMeta,
    nextTaskMeta,
    noActiveOrder,
    newCandidateMeta,
    filterName,
  } = props;

  React.useEffect(() => {
    if (filterName && activeRecordsName !== filterName) {
      setActiveRecordsName(filterName);
    }
  }, [filterName]);

  const noRXOrderTotal = noRXOrderMeta?.page?.total ?? 0;
  const noOrderTotal = noOrderMeta?.page?.total ?? 0;
  const expiringRewardsTotal = expiringRewardsMeta?.page?.total ?? 0;
  const recentlyInactiveTotal = recentlyInactiveMeta?.page?.total ?? 0;
  const premierCancellationsTotal = premierCancellationsMeta?.page?.total ?? 0;
  const expiringWellnessCreditsTotal =
    expiringWellnessCreditsMeta?.page?.total ?? 0;
  const nextTaskTotal = nextTaskMeta?.page?.total ?? 0;
  const noActiveOrderTotal = noActiveOrder?.page?.total ?? 0;
  const newCandidateTotal = newCandidateMeta?.page?.total ?? 0;

  const removeExpiringRewards = shouldReleaseFeature(
    get(featureFlag, 'ff_Remove_Expiring_Rewards', ''),
  );

  const {ts} = useTranslation('report.peopleToContact');

  const recordsName = React.useMemo(
    () =>
      activeRecordsName ||
      filterName ||
      findKey(
        CLIENT_TO_CONTACT_REPORT_ENUM,
        (arg: string) => arg === currentScreen,
      ) ||
      REPORT_CLIENT_TO_CONTACT_NO_ORDER,
    [activeRecordsName, filterName, currentScreen],
  );

  const currentTab = React.useMemo(() => {
    const fromActive = activeRecordsName
      ? CLIENT_TO_CONTACT_REPORT_ENUM[activeRecordsName]
      : undefined;
    const fromFilter = filterName
      ? CLIENT_TO_CONTACT_REPORT_ENUM[filterName]
      : undefined;
    const fromScreen =
      currentScreen &&
      Object.values(CLIENT_TO_CONTACT_REPORT_ENUM).includes(currentScreen)
        ? currentScreen
        : undefined;

    return fromActive || fromFilter || fromScreen;
  }, [activeRecordsName, filterName, currentScreen]);

  return (
    <NavigationHeader
      title={ts('title', 'People to Contact')}
      style={{
        base: styles.base,
        header: styles.header,
        arrangedIcons: styles.arrangedIcons,
        iconsGap: styles.iconsGap,
      }}>
      {/*  --- Qualtrics survey temporarily disabled for accessibility compliance ---
      <FeatureFeedback
        accessable={true}
        style={{base: styles.featureFeedback}}
        parentScreenName={SCREEN_NAMES.clientsToContact}
      /> */}
      <ReportDataProvider
        dataRenderer={PeopleToContact}
        dataRendererProps={{
          listHeaderComponent: ClientsListHeader,
          swipeEnabled: true,
          initialRouteName: currentTab,
          reportDefaultLoadingCount: 5,
          showFullReport: true,
          onShowProfile: generateOnRecordPress({
            recordsShowRecord,
          }),
        }}
        loadingData={{
          total:
            Number(noOrderTotal) +
            Number(noRXOrderTotal) +
            Number(!removeExpiringRewards ? expiringRewardsTotal : 0) +
            Number(recentlyInactiveTotal) +
            Number(premierCancellationsTotal) +
            Number(expiringWellnessCreditsTotal) +
            Number(nextTaskTotal) +
            Number(noActiveOrderTotal) +
            Number(newCandidateTotal),
        }}
        reportName={recordsName}
        reportIsLoading={false}
        currentScreenName={SCREEN_NAMES.clientsToContact}
      />
    </NavigationHeader>
  );
};

PeopleToContactContent.defaultProps = {
  filterName: '',
};
